import React, { useState, useEffect } from 'react';
import { SEO } from '../components/SEO';
import { FaDownload, FaFileAlt, FaChartLine, FaArrowRight, FaExternalLinkAlt, FaChevronDown, FaChevronUp, FaInfoCircle } from 'react-icons/fa';
import { Link } from 'react-router-dom';

const DownloadsModule3 = () => {
  // Reset scroll position when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const [expandedReviewIndex, setExpandedReviewIndex] = useState(null);
  
  const toggleReview = (index) => {
    setExpandedReviewIndex(expandedReviewIndex === index ? null : index);
  };

  const reviews = [
    {
      version: "9.0.33",
      date: "28 de marzo de 2023",
      changes: [
        "Mejoras en la integración con los módulos 1 y 2 para una experiencia más fluida",
        "Optimización del rendimiento en la generación de órdenes de compra y reportes",
        "Mejora en la interfaz para la gestión de proveedores y requisiciones",
        "Nueva funcionalidad para el seguimiento de entregas pendientes"
      ]
    },
    {
      version: "9.0.29",
      date: "15 de febrero de 2023",
      changes: [
        "Mejora en la visualización de datos de proveedores",
        "Corrección de errores en la importación de catálogos de precios",
        "Optimización en el rendimiento de búsquedas y filtrado",
        "Nuevas opciones de personalización en comparativas de precios"
      ]
    },
    {
      version: "9.0.24",
      date: "10 de enero de 2023",
      changes: [
        "Nueva funcionalidad para la gestión avanzada de inventarios",
        "Mejoras en la interfaz de usuario para agilizar procesos de compra",
        "Correcciones en el cálculo de costos y márgenes",
        "Actualización de componentes internos para mayor compatibilidad"
      ]
    }
  ];

  const downloads = [
    {
      version: "9.0.33",
      date: "28 de marzo de 2023",
      liteSize: "165 MB",
      liteUrl: "https://www.ecosoft.com.mx/descargas/opus22/M3_2022_33_20230328_Lite.zip",
      hasFullVersion: false
    },
    {
      version: "9.0.29",
      date: "15 de febrero de 2023",
      liteSize: "165 MB",
      liteUrl: "https://www.ecosoft.com.mx/descargas/opus22/M3_2022_29_20230215_Lite.zip",
      hasFullVersion: false
    },
    {
      version: "9.0.24",
      date: "10 de enero de 2023",
      liteSize: "165 MB",
      fullSize: "620 MB",
      liteUrl: "https://www.ecosoft.com.mx/descargas/opus22/M3_2022_24_20230110_Lite.zip",
      fullUrl: "https://www.ecosoft.com.mx/descargas/opus22/M3_2022_24_20230110_Full.zip",
      hasFullVersion: true
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <SEO
        title="Descargas OPUS 22 Módulo 3 | Ecosoft"
        description="Descargue las últimas versiones de OPUS 22 Módulo 3: Administración de Compras."
        name="DownloadsModule3"
        type="website"
      />
      
      {/* Hero Section */}
      <section id="overview" className="relative h-[350px] bg-gradient-to-r from-[#003171] to-[#0066CC]">
        <div className="absolute inset-0 bg-black/30"></div>
        <div className="container mx-auto px-4 h-full flex items-center pt-12 relative z-10">
          <div className="max-w-2xl text-white">
            <h1 className="text-4xl font-bold mb-2">OPUS 22 - Módulo 3</h1>
            <h2 className="text-2xl font-semibold mb-4">Administración de Compras</h2>
            <p className="text-lg mb-6">
              Descargue las últimas versiones del Módulo 3 de OPUS 22 para la gestión eficiente de compras en sus proyectos.
            </p>
            <Link to="/soporte/descargas" className="inline-flex items-center text-white hover:underline">
              <FaArrowRight className="mr-2 rotate-180" /> Volver al Centro de Descargas
            </Link>
          </div>
        </div>
      </section>
      
      {/* Main Content */}
      <section className="py-12 px-6">
        <div className="container mx-auto max-w-6xl">
          <div className="bg-white p-8 rounded-lg shadow-lg border border-gray-200 mb-8">
            <div className="flex items-center mb-6">
              <div className="bg-[#0055B8]/10 p-3 rounded-full mr-4">
                <FaInfoCircle className="text-[#0055B8] text-2xl" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-[#1A2B39]">Información importante</h2>
                <p className="text-gray-600">Sobre las descargas de OPUS 22 - Módulo 3</p>
              </div>
            </div>
            
            <div className="mb-6 text-gray-700">
              <p className="mb-4">
                <strong>¿Qué revisión debo descargar?</strong>
              </p>
              <p className="mb-4">
                OPUS se adquiere bajo un esquema de suscripción, con fecha de caducidad. Esto quiere decir que puedes trabajar con revisiones con fecha de publicación igual o anterior a la fecha de caducidad de tu suscripción.
              </p>
              <p className="mb-4">
                La siguiente lista despliega todas las revisiones publicadas hasta el momento. Descarga e instala la revisión más reciente cuya fecha sea menor o igual a la fecha de caducidad de tu suscripción.
              </p>
              <p>
                El Módulo 3 de OPUS 22 te permite administrar eficientemente el proceso de compras, desde la requisición hasta la recepción de materiales, con un control detallado de proveedores, precios y tiempos de entrega.
              </p>
            </div>
          </div>
          
          {/* Downloads Section */}
          <h2 className="text-3xl font-bold mb-6 text-[#1A2B39]">Descargas disponibles</h2>
          
          <div className="mb-12">
            <div className="overflow-hidden rounded-lg shadow">
              <table className="w-full">
                <thead>
                  <tr className="bg-[#0055B8] text-white">
                    <th className="px-6 py-4 text-left">Revisión</th>
                    <th className="px-6 py-4 text-left">Fecha</th>
                    <th className="px-6 py-4 text-left">Descargas</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {downloads.map((download, index) => (
                    <tr key={index} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap font-semibold">
                        {download.version}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {download.date}
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex flex-wrap gap-3">
                          <a 
                            href={download.liteUrl} 
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center px-4 py-2 bg-[#0055B8] text-white rounded hover:bg-[#003a80] transition-colors"
                          >
                            <FaDownload className="mr-2" /> Descarga Lite ({download.liteSize})
                          </a>
                          
                          {download.hasFullVersion && (
                            <a 
                              href={download.fullUrl} 
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center px-4 py-2 bg-[#1A2B39] text-white rounded hover:bg-[#0d1824] transition-colors"
                            >
                              <FaDownload className="mr-2" /> Descarga Completa ({download.fullSize})
                            </a>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="text-sm text-gray-600 mt-3">
              * Descarga e instala el paquete Lite si ya tienes instalado OPUS MÓDULO 3 en versión 21 ó la versión de lanzamiento de OPUS22.
            </p>
          </div>
          
          {/* Changes and Improvements Section */}
          <h2 className="text-3xl font-bold mb-6 text-[#1A2B39]">Cambios y mejoras</h2>
          
          <div className="space-y-4 mb-12">
            {reviews.map((review, index) => (
              <div key={index} className="bg-white border border-gray-200 rounded-lg shadow">
                <div 
                  className="p-4 flex justify-between items-center cursor-pointer hover:bg-gray-50"
                  onClick={() => toggleReview(index)}
                >
                  <div>
                    <h3 className="font-bold text-lg text-[#1A2B39]">Revisión {review.version}</h3>
                    <p className="text-gray-600">Fecha: {review.date}</p>
                  </div>
                  <div>
                    {expandedReviewIndex === index ? (
                      <FaChevronUp className="text-[#0055B8]" />
                    ) : (
                      <FaChevronDown className="text-[#0055B8]" />
                    )}
                  </div>
                </div>
                
                {expandedReviewIndex === index && (
                  <div className="p-4 pt-0 border-t border-gray-200">
                    <ul className="list-disc pl-6 space-y-1 text-gray-700 mt-3">
                      {review.changes.map((change, changeIndex) => (
                        <li key={changeIndex}>{change}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            ))}
          </div>
          
          {/* Features Section */}
          <h2 className="text-3xl font-bold mb-6 text-[#1A2B39]">Características principales</h2>
          
          <div className="grid md:grid-cols-2 gap-6 mb-12">
            <div className="bg-white p-6 rounded-lg shadow border border-gray-200">
              <h3 className="text-xl font-bold mb-4 text-[#0055B8]">Gestión de proveedores</h3>
              <p className="mb-4 text-gray-600">
                Administra una base de datos completa de proveedores con información de contacto, historial de compras, condiciones comerciales y evaluación de desempeño.
              </p>
            </div>
            
            <div className="bg-white p-6 rounded-lg shadow border border-gray-200">
              <h3 className="text-xl font-bold mb-4 text-[#0055B8]">Requisiciones y órdenes de compra</h3>
              <p className="mb-4 text-gray-600">
                Genera requisiciones a partir de los presupuestos aprobados y crea órdenes de compra automáticamente, manteniendo la trazabilidad completa del proceso.
              </p>
            </div>
            
            <div className="bg-white p-6 rounded-lg shadow border border-gray-200">
              <h3 className="text-xl font-bold mb-4 text-[#0055B8]">Comparativas de precios</h3>
              <p className="mb-4 text-gray-600">
                Realiza comparativas de precios entre diferentes proveedores para obtener las mejores condiciones en tus compras, con criterios personalizados de selección.
              </p>
            </div>
            
            <div className="bg-white p-6 rounded-lg shadow border border-gray-200">
              <h3 className="text-xl font-bold mb-4 text-[#0055B8]">Control de inventarios</h3>
              <p className="mb-4 text-gray-600">
                Controla las existencias en almacén, registra entradas y salidas de materiales, y genera reportes detallados de movimientos y saldos por proyecto.
              </p>
            </div>
          </div>
          
          {/* Additional Resources */}
          <h2 className="text-3xl font-bold mb-6 text-[#1A2B39]">Recursos adicionales</h2>
          
          <div className="grid md:grid-cols-2 gap-6 mb-12">
            <div className="bg-white p-6 rounded-lg shadow border border-gray-200">
              <h3 className="text-xl font-bold mb-4 text-[#0055B8]">Guías y manuales</h3>
              <p className="mb-4 text-gray-600">
                Para obtener un manual actualizado, envíanos un correo a sgarcia@ecosoft.com.mx desde tu cuenta de correo asociada a tu suscripción vigente.
              </p>
            </div>
            
            <div className="bg-white p-6 rounded-lg shadow border border-gray-200">
              <h3 className="text-xl font-bold mb-4 text-[#0055B8]">Soporte técnico</h3>
              <p className="mb-4 text-gray-600">
                Si necesitas ayuda con la instalación o uso de OPUS, ponte en contacto con nuestro equipo de soporte técnico.
              </p>
              <p className="text-[#0055B8] font-bold">
                WhatsApp Soporte: (55) 7965.8522
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default DownloadsModule3; 